<template>
    <div>
        <a-modal
            :visible="true"
            width="85vw"
            :footer="null"
            :closable="false"
            :destroy-on-close="true"
            centered
            wrap-class-name="vpm-wrap"
            @cancel="$emit('close')"
        >
            <div class="vpm-modal">
                <div class="vpm-head">
                    <div>
                        <h3>Produtos pendentes</h3>
                        <div class="vpm-sub">{{ storeName }} · {{ providerName }}<template v-if="sellerName"> · {{ sellerName }}</template></div>
                    </div>
                    <a-tooltip title="Fechar">
                        <a-button class="vpm-x" @click="$emit('close')">
                            <span class="material-icons-outlined">close</span>
                        </a-button>
                    </a-tooltip>
                </div>

                <div class="vpm-tools">
                    <a-input v-model="search" class="vpm-search" placeholder="Buscar produto..." allow-clear>
                        <a-icon slot="prefix" type="search" />
                    </a-input>
                </div>

                <cc-loader-full v-if="loading" />
                <div class="vpm-body" v-else>
                    <a-table
                        size="small"
                        :columns="columns"
                        :data-source="filteredProducts"
                        :pagination="false"
                        row-key="id"
                        :scroll="{ x: 1260 }"
                        :locale="{ emptyText: 'Nenhum produto pendente.' }"
                    >
                        <template slot="nome" slot-scope="text, product">
                            <div class="vpm-pname">{{ product.name }}</div>
                            <div class="vpm-pean" v-if="product.ean || product.internal_code">
                                <template v-if="product.ean">EAN {{ product.ean }}</template>
                                <template v-if="product.ean && product.internal_code"> · </template>
                                <template v-if="product.internal_code">Cód. {{ product.internal_code }}</template>
                            </div>
                        </template>

                        <template slot="pedido" slot-scope="text, product">
                            <input
                                type="number"
                                min="0"
                                class="vpm-qty-input"
                                v-model.number="product.quantity"
                                @blur="saveQuantity(product)"
                            />
                        </template>

                        <template slot="digitacao" slot-scope="text, product">
                            {{ product.offer_price | customCurrency }}
                        </template>

                        <template slot="ultimaCompra" slot-scope="text, product">
                            <span v-if="product.last_purchase">{{ product.last_purchase | customCurrency }}</span>
                            <span v-else>—</span>
                        </template>

                        <template slot="diferenca" slot-scope="text, product">
                            <span v-if="product.last_purchase" :class="deltaClass(product)">
                                {{ (product.offer_price - product.last_purchase) | customCurrency }}
                            </span>
                            <span v-else>—</span>
                        </template>

                        <template slot="variacao" slot-scope="text, product">
                            <span v-if="product.last_purchase" :class="deltaClass(product)">
                                {{ variation(product).toFixed(1) }}%
                            </span>
                            <span v-else>—</span>
                        </template>

                        <template slot="total" slot-scope="text, product">
                            {{ product.total | customCurrency }}
                        </template>

                        <template slot="acoes" slot-scope="text, product">
                            <a-tooltip title="Análise de giro">
                                <a-button type="link" size="small" @click="openGiro(product)">
                                    <span class="material-symbols-outlined">query_stats</span>
                                </a-button>
                            </a-tooltip>
                            <a-tooltip title="Negociar preço">
                                <a-button type="link" size="small" @click="openNegotiation(product)">
                                    <span class="material-symbols-outlined">handshake</span>
                                </a-button>
                            </a-tooltip>
                            <a-tooltip title="Alterar embalagem">
                                <a-button type="link" size="small" @click="openWrap(product)">
                                    <span class="material-symbols-outlined">inventory_2</span>
                                </a-button>
                            </a-tooltip>
                            <a-tooltip title="Histórico de pedidos">
                                <a-button type="link" size="small" @click="openHistory(product)">
                                    <span class="material-icons-outlined vpm-history-icon">assignment</span>
                                </a-button>
                            </a-tooltip>
                        </template>
                    </a-table>
                </div>

                <div class="vpm-foot">
                    <a-button class="vpm-btn-ghost" @click="$emit('close')">Fechar</a-button>
                </div>
            </div>
        </a-modal>

        <NegotiationModal
            v-if="negotiationModal.open"
            :product="negotiationModal.product"
            :typing="negotiationModal.winner"
            :quotation_id="quotationId"
            @close="closeNegotiation"
        />

        <ProductWrapModal
            v-if="wrapModal.open"
            :product="wrapModal.product"
            :salesman="wrapModal.winner"
            @close="closeWrap"
        />

        <ProductOrdersModal
            v-if="historyModal.open"
            :product_id="historyModal.productId"
            :product_name="historyModal.productName"
            @close="historyModal.open = false"
        />
    </div>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestsService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import NegotiationModal from "@/modals/negotiate-offer/negotiate-offer.modal.vue";
import ProductWrapModal from "@/modals/product-wrap/product-wrap.modal.vue";
import ProductOrdersModal from "@/modals/products/orders";

export default {
    name: "ViewProductsModal",
    components: { NegotiationModal, ProductWrapModal, ProductOrdersModal },
    props: {
        quotationId: [String, Number],
        storeId: [String, Number],
        storeName: String,
        providerId: [String, Number],
        providerName: String,
        sellerId: [String, Number],
        sellerName: String,
    },
    data() {
        return {
            svc: new QuotationService(),
            v3Svc: new RequestsService(),
            products: [],
            loading: true,
            search: "",
            negotiationModal: {
                open: false,
                winner: null,
                product: null,
            },
            wrapModal: {
                open: false,
                winner: null,
                product: null,
            },
            historyModal: {
                open: false,
                productId: null,
                productName: "",
            },
        };
    },
    computed: {
        columns() {
            return [
                { title: "Produto", key: "nome", width: 220, scopedSlots: { customRender: "nome" } },
                { title: "Qtd. Emb.", key: "packing", width: 90, align: "center", customRender: (t, p) => p.packing || "—" },
                { title: "Pedido", key: "pedido", width: 90, align: "center", scopedSlots: { customRender: "pedido" } },
                { title: "Und. compra", key: "purchase_unity", width: 100, align: "center", customRender: (t, p) => p.purchase_unity || "—" },
                { title: "Estoque", key: "inventory", width: 90, align: "center", customRender: (t, p) => (p.inventory != null ? p.inventory : "—") },
                { title: "Digitação", key: "digitacao", width: 100, align: "right", scopedSlots: { customRender: "digitacao" } },
                { title: "Últ. Compra", key: "ultimaCompra", width: 100, align: "right", scopedSlots: { customRender: "ultimaCompra" } },
                { title: "Diferença", key: "diferenca", width: 100, align: "right", scopedSlots: { customRender: "diferenca" } },
                { title: "Variação", key: "variacao", width: 90, align: "right", scopedSlots: { customRender: "variacao" } },
                { title: "Total", key: "total", width: 110, align: "right", scopedSlots: { customRender: "total" } },
                { title: "Ações", key: "acoes", width: 170, scopedSlots: { customRender: "acoes" } },
            ];
        },
        filteredProducts() {
            if (!this.search) return this.products;
            const term = this.search.toLowerCase();
            return this.products.filter((p) => (p.name || "").toLowerCase().includes(term));
        },
    },
    created() {
        this.loadProducts();
    },
    methods: {
        loadProducts() {
            this.loading = true;
            this.v3Svc
                .pre_orders_products(this.quotationId, {
                    store_id: this.storeId,
                    provider_id: this.providerId,
                    seller_id: this.sellerId,
                })
                .then((response) => response.data)
                .then((data) => {
                    this.products = data || [];
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        deltaClass(product) {
            if (product.offer_price > product.last_purchase) return "vpm-up";
            if (product.offer_price < product.last_purchase) return "vpm-down";
            return "";
        },
        variation(product) {
            if (!product.last_purchase) return 0;
            return ((product.offer_price - product.last_purchase) / product.last_purchase) * 100;
        },
        saveQuantity(product) {
            const quantidade = Math.max(0, Number(product.quantity) || 0);
            this.svc
                .sendQuantidade({
                    numerocotacao: this.quotationId,
                    quantidade: quantidade || "0",
                    vendedor: this.sellerId,
                    produto: product.id,
                    loja: this.storeId,
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        openGiro(product) {
            this.$emit("giro", {
                id: product.id,
                provider_id: this.providerId,
                seller_id: this.sellerId,
                name: product.name,
                packing: product.packing,
                is_generic: 0,
                main_product_id: null,
                internal_code: null,
                requested_quantity: product.quantity,
            });
        },
        openNegotiation(product) {
            this.negotiationModal = {
                open: true,
                winner: {
                    idVendedor: this.sellerId,
                    idFornecedor: this.providerId,
                    pro_id: product.id,
                    valor: product.offer_price,
                    nomeVendedor: this.sellerName,
                },
                product: {
                    produtoDescricao: product.name,
                    pro_observacao: null,
                    observacao: null,
                    ultimaCompra: product.last_purchase,
                },
            };
        },
        closeNegotiation() {
            this.negotiationModal.open = false;
            this.loadProducts();
        },
        openWrap(product) {
            this.wrapModal = {
                open: true,
                winner: {
                    idVendedor: this.sellerId,
                    idFornecedor: this.providerId,
                    pro_id: product.id,
                    embalagem: product.packing,
                },
                product: {
                    descricaoProduto: product.name,
                    embalagem: product.packing,
                    idProduto: product.id,
                    flag_generico: 0,
                    pro_principal: null,
                },
            };
        },
        closeWrap() {
            this.wrapModal.open = false;
            this.loadProducts();
        },
        openHistory(product) {
            this.historyModal = {
                open: true,
                productId: Number(product.id),
                productName: product.name,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.vpm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.vpm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.vpm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.vpm-x.ant-btn,
.vpm-x.ant-btn:focus,
.vpm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.vpm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.vpm-tools {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 0 20px 12px;
}
.vpm-search {
    flex: 1;
    max-width: 280px;

    ::v-deep(.ant-input) {
        height: 36px;
    }
}
.vpm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.vpm-pname {
    font-weight: 600;
    font-size: 13px;
    color: #262626;
}
.vpm-pean {
    font-size: 11px;
    color: #8c8c8c;
}
.vpm-qty-input {
    width: 64px;
    height: 30px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    text-align: center;
    font-size: 13px;

    &:focus {
        border-color: var(--primary-color);
        outline: none;
    }
}
.vpm-history-icon {
    color: #4267b2;
}
.vpm-up {
    color: #cf1322;
    font-weight: 600;
}
.vpm-down {
    color: #389e0d;
    font-weight: 600;
}
.vpm-foot {
    display: flex;
    justify-content: flex-end;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.vpm-btn-ghost.ant-btn,
.vpm-btn-ghost.ant-btn:focus,
.vpm-btn-ghost.ant-btn:active {
    height: 38px;
    padding: 0 18px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #434343;
    font-weight: 500;
    font-size: 14px;
    box-shadow: none;
}
.vpm-btn-ghost.ant-btn:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
}
</style>

<style lang="scss">
.vpm-wrap {
    .ant-modal {
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
